import React, { useState, useCallback, useEffect, useRef } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ResizableRowProps {
  /** Fraction of width each column occupies (must sum to 1). */
  columnSizes: number[];
  /** Callback when column sizes change. */
  onColumnSizesChange: (sizes: number[]) => void;
  /** Minimum column width in px. */
  minColumnWidth?: number;
  /** Gap between columns in px. */
  gap?: number;
  /** The content for each column. */
  children: React.ReactNode[];
}

interface ResizableGridProps {
  /** Fraction of total height each row occupies (must sum to 1). */
  rowSizes: number[];
  /** Callback when row sizes change. */
  onRowSizesChange: (sizes: number[]) => void;
  /** Minimum row height in px. */
  minRowHeight?: number;
  /** Gap between rows in px. */
  gap?: number;
  /** Each child is a ResizableRow. */
  children: React.ReactNode[];
}

// ─── Horizontal drag handle (between columns in a row) ───────────────────────

const ColumnDragHandle: React.FC<{
  onDragStart: (startX: number) => void;
  onDragMove: (clientX: number) => void;
  onDragEnd: () => void;
}> = ({ onDragStart, onDragMove, onDragEnd }) => {
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      onDragStart(e.clientX);

      const handleMouseMove = (ev: MouseEvent) => {
        onDragMove(ev.clientX);
      };

      const handleMouseUp = () => {
        onDragEnd();
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [onDragStart, onDragMove, onDragEnd]
  );

  return (
    <div
      onMouseDown={handleMouseDown}
      className="shrink-0 cursor-col-resize flex items-center justify-center group z-10"
      style={{ width: 12 }}
    >
      <div className="w-1 h-8 rounded-full bg-slate-200 group-hover:bg-primary/50 group-active:bg-primary transition-colors" />
    </div>
  );
};

// ─── Vertical drag handle (between rows) ─────────────────────────────────────

const RowDragHandle: React.FC<{
  onDragStart: (startY: number) => void;
  onDragMove: (clientY: number) => void;
  onDragEnd: () => void;
}> = ({ onDragStart, onDragMove, onDragEnd }) => {
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      onDragStart(e.clientY);

      const handleMouseMove = (ev: MouseEvent) => {
        onDragMove(ev.clientY);
      };

      const handleMouseUp = () => {
        onDragEnd();
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [onDragStart, onDragMove, onDragEnd]
  );

  return (
    <div
      onMouseDown={handleMouseDown}
      className="shrink-0 cursor-row-resize flex justify-center items-center group z-10"
      style={{ height: 12 }}
    >
      <div className="h-1 w-8 rounded-full bg-slate-200 group-hover:bg-primary/50 group-active:bg-primary transition-colors" />
    </div>
  );
};

// ─── ResizableRow ─────────────────────────────────────────────────────────────

export const ResizableRow: React.FC<ResizableRowProps> = ({
  columnSizes,
  onColumnSizesChange,
  minColumnWidth = 180,
  gap = 8,
  children,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const childArray = React.Children.toArray(children);

  // Use refs to avoid stale closures during drag
  const sizesRef = useRef(columnSizes);
  sizesRef.current = columnSizes;
  const dragStartX = useRef(0);
  const dragStartSizes = useRef<number[]>([]);

  const handleDragStart = useCallback(
    (index: number, startX: number) => {
      dragStartX.current = startX;
      dragStartSizes.current = [...sizesRef.current];
    },
    []
  );

  const handleDragMove = useCallback(
    (index: number, clientX: number) => {
      const container = containerRef.current;
      if (!container) return;
      const totalWidth = container.offsetWidth;
      if (totalWidth === 0) return;

      const delta = clientX - dragStartX.current;
      const deltaFraction = delta / totalWidth;
      const sizes = [...dragStartSizes.current];

      const minFraction = minColumnWidth / totalWidth;

      const leftNew = sizes[index] + deltaFraction;
      const rightNew = sizes[index + 1] - deltaFraction;

      if (leftNew < minFraction || rightNew < minFraction) return;

      sizes[index] = leftNew;
      sizes[index + 1] = rightNew;
      onColumnSizesChange(sizes);
    },
    [onColumnSizesChange, minColumnWidth]
  );

  const handleDragEnd = useCallback(() => {}, []);

  return (
    <div ref={containerRef} className="flex flex-1 min-h-0 min-w-0 items-stretch" style={{ gap }}>
      {childArray.map((child, i) => (
        <React.Fragment key={`col-${i}`}>
          <div
            className="min-w-0 flex flex-col"
            style={{
              flex: `${columnSizes[i]} 1 0%`,
            }}
          >
            {child}
          </div>
          {i < childArray.length - 1 && (
            <ColumnDragHandle
              onDragStart={(x) => handleDragStart(i, x)}
              onDragMove={(x) => handleDragMove(i, x)}
              onDragEnd={handleDragEnd}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// ─── ResizableGrid (vertical rows) ───────────────────────────────────────────

export const ResizableGrid: React.FC<ResizableGridProps> = ({
  rowSizes,
  onRowSizesChange,
  minRowHeight = 150,
  gap = 8,
  children,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const childArray = React.Children.toArray(children);

  // Use refs to avoid stale closures during drag
  const sizesRef = useRef(rowSizes);
  sizesRef.current = rowSizes;
  const dragStartY = useRef(0);
  const dragStartSizes = useRef<number[]>([]);

  const handleDragStart = useCallback(
    (index: number, startY: number) => {
      dragStartY.current = startY;
      dragStartSizes.current = [...sizesRef.current];
    },
    []
  );

  const handleDragMove = useCallback(
    (index: number, clientY: number) => {
      const container = containerRef.current;
      if (!container) return;
      const totalHeight = container.offsetHeight;
      if (totalHeight === 0) return;

      const delta = clientY - dragStartY.current;
      const deltaFraction = delta / totalHeight;
      const sizes = [...dragStartSizes.current];

      const minFraction = minRowHeight / totalHeight;

      const topNew = sizes[index] + deltaFraction;
      const bottomNew = sizes[index + 1] - deltaFraction;

      if (topNew < minFraction || bottomNew < minFraction) return;

      sizes[index] = topNew;
      sizes[index + 1] = bottomNew;
      onRowSizesChange(sizes);
    },
    [onRowSizesChange, minRowHeight]
  );

  const handleDragEnd = useCallback(() => {}, []);

  return (
    <div ref={containerRef} className="flex flex-col flex-1 min-h-0" style={{ gap }}>
      {childArray.map((child, i) => (
        <React.Fragment key={`row-${i}`}>
          <div
            className="overflow-hidden min-h-0 flex flex-col"
            style={{
              flexGrow: rowSizes[i],
              flexShrink: 0,
              flexBasis: 0,
            }}
          >
            {child}
          </div>
          {i < childArray.length - 1 && (
            <RowDragHandle
              onDragStart={(y) => handleDragStart(i, y)}
              onDragMove={(y) => handleDragMove(i, y)}
              onDragEnd={handleDragEnd}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// ─── localStorage persistence hook ───────────────────────────────────────────

const STORAGE_KEY = 'spectrum-dashboard-layout';

interface DashboardLayout {
  rowSizes: number[];
  row1Cols: number[];
  row2Cols: number[];
  row3Cols: number[];
}

export const DEFAULT_LAYOUT: DashboardLayout = {
  rowSizes: [0.28, 0.40, 0.32],
  row1Cols: [1 / 3, 1 / 3, 1 / 3],
  row2Cols: [0.6, 0.4],
  row3Cols: [0.5, 0.5],
};

export function useDashboardLayout() {
  const [layout, setLayout] = useState<DashboardLayout>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as DashboardLayout;
        if (
          parsed.rowSizes?.length === 3 &&
          parsed.row1Cols?.length === 3 &&
          parsed.row2Cols?.length === 2 &&
          parsed.row3Cols?.length === 2
        ) {
          return parsed;
        }
      }
    } catch { /* ignore */ }
    return DEFAULT_LAYOUT;
  });

  // Debounced save to localStorage
  const saveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    saveTimeout.current = setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
    }, 300);
    return () => {
      if (saveTimeout.current) clearTimeout(saveTimeout.current);
    };
  }, [layout]);

  const updateRowSizes = useCallback((sizes: number[]) => {
    setLayout((prev) => ({ ...prev, rowSizes: sizes }));
  }, []);

  const updateRow1Cols = useCallback((sizes: number[]) => {
    setLayout((prev) => ({ ...prev, row1Cols: sizes }));
  }, []);

  const updateRow2Cols = useCallback((sizes: number[]) => {
    setLayout((prev) => ({ ...prev, row2Cols: sizes }));
  }, []);

  const updateRow3Cols = useCallback((sizes: number[]) => {
    setLayout((prev) => ({ ...prev, row3Cols: sizes }));
  }, []);

  const resetLayout = useCallback(() => {
    setLayout(DEFAULT_LAYOUT);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return {
    layout,
    updateRowSizes,
    updateRow1Cols,
    updateRow2Cols,
    updateRow3Cols,
    resetLayout,
  };
}
